
import { useCallback } from "react";
import { Form, FormField } from "@/components/dashboard/shops/types";
import { updateForm, createForm } from "@/lib/supabase/forms";
import { toast } from "sonner";
import { useOrganizationId } from "@/components/dashboard/products/hooks/useOrganizationId";

export const useFormSave = (
  form: Form | null,
  formState: {
    setIsSaving: (value: boolean) => void;
    setIsEditing: (value: boolean) => void;
    formName: string;
    ctaButtonText: string;
    formProductIds: string[];
    // Add state for new fields
    phone?: string;
    customerName?: string;
    email?: string;
    country?: string;
    region?: string;
    municipality?: string;
    address?: string;
    externalId?: string;
  },
  formFields: FormField[]
) => {
  const { organizationId } = useOrganizationId();

  const handleSave = useCallback(async () => {
    try {
      formState.setIsSaving(true);
      
      // Valider les champs du formulaire avant la sauvegarde
      const processedFields = formFields.map(field => {
        // Identifier les champs de téléphone par nom ou type actuel
        const isPhoneField = field.type === 'phone' || 
                           field.name === 'customerPhone' || 
                           field.name === 'Téléphone' ||
                           field.name.toLowerCase().includes('phone') || 
                           field.name.toLowerCase().includes('téléphone');
                           
        // Garantir que les champs de téléphone sont toujours de type 'phone', requis et activés                   
        if (isPhoneField) {
          return { ...field, type: 'phone' as FormField['type'], required: true, enabled: true };
        }
        
        // Filtrer les champs désactivés - ils ne devraient pas apparaître comme activés dans l'interface
        if (!field.enabled) {
          return { ...field, enabled: false };
        }
        
        return field;
      }) as FormField[];
      
      const formData: Partial<Form> = {
        name: formState.formName,
        cta_button_text: formState.ctaButtonText,
        fields: processedFields,
        product_ids: formState.formProductIds, // Gardons cette information dans la BDD même si on ne l'affiche plus
        // Include new customer fields
        phone: formState.phone || '',
        customer_name: formState.customerName || '',
        email: formState.email || '',
        country: formState.country || '',
        region: formState.region || '',
        municipality: formState.municipality || '',
        address: formState.address || '',
        external_id: formState.externalId || '',
      };
      
      console.log("Saving form data:", formData);
      
      let result;
      
      if (form?.id) {
        // Update existing form
        result = await updateForm(form.id, formData);
        
        if (result.success) {
          toast.success("Formulaire mis à jour avec succès", {
            description: "Les paramètres du formulaire ont été enregistrés."
          });
          formState.setIsEditing(false);
        } else {
          throw new Error(result.error?.message || "Une erreur est survenue lors de la mise à jour du formulaire");
        }
      } else {
        // Create new form
        if (!organizationId) {
          throw new Error("ID d'organisation manquant");
        }
        
        // Get shop_id from form if available
        // IMPORTANT: Make sure we're using a valid shop_id (not "shop-settings" from URL)
        let shopId = form?.shop_id;
        
        // If we don't have a valid shop_id, throw an error
        if (!shopId || typeof shopId !== 'string' || 
            !shopId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          throw new Error("L'ID de la boutique n'est pas valide. Impossible de créer le formulaire.");
        }
        
        const newForm: Partial<Form> = {
          ...formData,
          organization_id: organizationId,
          shop_id: shopId,
        };
        
        result = await createForm(newForm);
        
        if (result.success && result.data) {
          toast.success("Formulaire créé avec succès", {
            description: "Le nouveau formulaire a été enregistré."
          });
          formState.setIsEditing(false);
          return result.data;
        } else {
          throw new Error(result.error?.message || "Une erreur est survenue lors de la création du formulaire");
        }
      }
      
      return result.data;
    } catch (error: any) {
      console.error("Form save error:", error);
      toast.error("Erreur", {
        description: error.message || "Une erreur est survenue lors de la sauvegarde du formulaire"
      });
      return null;
    } finally {
      formState.setIsSaving(false);
    }
  }, [form, formFields, formState, organizationId]);

  return { handleSave };
};
